/**
 * Godot's normal map read: red and green only, with blue rebuilt as `sqrt(max(0, 1 - dot(xy, xy)))`
 * (`scene_forward_clustered.glsl:1437-1438`), since an RGTC import stores no blue. three rebuilds
 * blue the same way under `USE_PACKED_NORMALMAP`, which it defines itself only for an RG format.
 * The map is read in a frame whose tangent and binormal have unit length, as Godot's are. The
 * tangent-space read is one function, so the roughness limiter can read a neighbouring texel.
 */
import { installChunkEdits } from '../shaderPatch/chunkPatch';
import { defining } from '../materialProgramInputs';
const PARS_CHUNK = 'normalmap_pars_fragment';
const MAPS_CHUNK = 'normal_fragment_maps';
const BEGIN_CHUNK = 'normal_fragment_begin';
/**
 * three's frame scales both axes by the longer one, so a quad with a UV density that differs per
 * axis squashes the shorter. Godot normalises the tangent and the binormal each
 * (`scene_forward_clustered.glsl:1437-1442`).
 */
const UNIT_TANGENT_FRAME_EDIT = {
    chunk: PARS_CHUNK,
    three: `float det = max( dot( T, T ), dot( B, B ) );
\t\tfloat scale = ( det == 0.0 ) ? 0.0 : inversesqrt( det );
\t\treturn mat3( T * scale, B * scale, N );`,
    godot: `float lengthT = length( T );
\t\tfloat lengthB = length( B );
\t\treturn mat3( lengthT == 0.0 ? vec3( 0.0 ) : T / lengthT, lengthB == 0.0 ? vec3( 0.0 ) : B / lengthB, N );`,
};
export const GODOT_NORMAL_MAP_READ = defining('USE_PACKED_NORMALMAP');
/**
 * The read a glTF material needs, which Godot imports as a StandardMaterial3D
 * (`gltf_document.cpp:2939-2940`): Godot's when it has a normal map, else none.
 */
export function importedNormalMapRead(material) {
    return material.normalMap ? GODOT_NORMAL_MAP_READ : undefined;
}
const THREE_NON_PERTURBED_NORMAL = 'vec3 nonPerturbedNormal = normal;';
/** The map UV's screen gradients, once for the pixel's read and the roughness limiter's. */
const GODOT_UV_GRADIENTS = `${THREE_NON_PERTURBED_NORMAL}
#ifdef USE_NORMALMAP_TANGENTSPACE
	vec2 godotUvDx = dFdx( vNormalMapUv );
	vec2 godotUvDy = dFdy( vNormalMapUv );
#endif`;
const THREE_NORMAL_SCALE = 'uniform vec2 normalScale;\n#endif';
/**
 * The tangent-space read as one function of a texel, so the roughness limiter
 * (`godotLightFunction.ts`) reads a neighbouring texel exactly as the pixel does.
 * `GODOT_NORMAL_TEXEL( step )` reads the map `step` pixels along the screen from this one, at this
 * pixel's gradients, as `textureGrad` takes them: the limiter's offset UV has none of its own. A
 * triplanar mapping replaces the read (`godotTriplanar.ts`).
 */
const GODOT_MAP_NORMAL = /* glsl */ `uniform vec2 normalScale;
#endif
#ifdef USE_NORMALMAP_TANGENTSPACE
	#define GODOT_NORMAL_TEXEL( step ) textureGrad( normalMap, vNormalMapUv + ( step ).x * godotUvDx + ( step ).y * godotUvDy, godotUvDx, godotUvDy )
	vec3 godotMapNormal( const in vec4 texel, const in mat3 tbn ) {
		vec3 mapN = texel.xyz * 2.0 - 1.0;
		#if defined( USE_PACKED_NORMALMAP )
			mapN = vec3( mapN.xy, sqrt( saturate( 1.0 - dot( mapN.xy, mapN.xy ) ) ) );
		#endif
		mapN.xy *= normalScale;
		return normalize( tbn * mapN );
	}
#endif`;
const THREE_TANGENT_READ = `	vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;
	#if defined( USE_PACKED_NORMALMAP )
		mapN = vec3( mapN.xy, sqrt( saturate( 1.0 - dot( mapN.xy, mapN.xy ) ) ) );
	#endif
	mapN.xy *= normalScale;
	normal = normalize( tbn * mapN );`;
const GODOT_TANGENT_READ = '\tnormal = godotMapNormal( GODOT_NORMAL_TEXEL( vec2( 0.0 ) ), tbn );';
const GODOT_NORMAL_MAP_EDITS = [
    { chunk: PARS_CHUNK, three: THREE_NORMAL_SCALE, godot: GODOT_MAP_NORMAL },
    { chunk: BEGIN_CHUNK, three: THREE_NON_PERTURBED_NORMAL, godot: GODOT_UV_GRADIENTS },
    { chunk: MAPS_CHUNK, three: THREE_TANGENT_READ, godot: GODOT_TANGENT_READ },
    // Every program that builds the frame from the screen derivatives: a normal map reads it, and so
    // does anisotropy, whose lobe turns with the tangent.
    UNIT_TANGENT_FRAME_EDIT,
];
/** The text that proves the patch installed. */
const NORMAL_MAP_READ_MARK = 'vec3 godotMapNormal(';
/**
 * Replaces three's tangent-space normal read for every program compiled after the call. A three
 * release that rewrites the lines keeps its own chunks, and `godotNormalMap.test.ts` fails on it.
 * True when the chunks hold the patch after the call.
 */
export function installGodotNormalMapRead() {
    return installChunkEdits({
        edits: GODOT_NORMAL_MAP_EDITS,
        mark: NORMAL_MAP_READ_MARK,
        patch: 'normal map read',
    });
}
//# sourceMappingURL=godotNormalMap.js.map